import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";

const STATUSES = ["open", "in_progress", "closed"];

export default function ServiceRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState("");
  const [form, setForm] = useState({ category: "Statement Request", customer_id: "", priority: "medium", target_date: "", description: "" });
  const canEdit = ["relationship_manager", "operations", "administrator", "customer"].includes(user.role);
  const isCustomer = user.role === "customer";

  const load = () => api.get("/service-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  if (!rows) return <Spinner />;

  const create = async () => {
    try { await api.post("/service-requests", { ...form, customer_id: isCustomer ? "self" : form.customer_id }); toast.success("Service request created"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (id, status) => { await api.put(`/service-requests/${id}`, { status }); toast.success("Updated"); load(); };
  const escalate = async (id) => { await api.put(`/service-requests/${id}`, { escalated: true, priority: "high" }); toast.success("Escalated"); load(); };
  const addComment = async () => {
    if (!comment) return;
    await api.post(`/service-requests/${active.id}/comment`, { text: comment });
    setComment(""); const r = await api.get("/service-requests"); setRows(r.data);
    setActive(r.data.find((x) => x.id === active.id));
  };

  return (
    <div>
      <PageHeader overline="Servicing" title="Service Requests" subtitle="Category, owner, priority, SLA target, escalation and closure."
        testid="sr-header"
        actions={canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="gap-1" data-testid="new-sr-btn"><Plus className="h-4 w-4" /> New Request</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Service Request</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="sr-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Statement Request", "KYC Update", "Transaction Query", "Address Change", "Complaint", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                {!isCustomer && (
                <div><Label>Customer</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger data-testid="sr-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["low", "medium", "high"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Target date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} /></div>
                </div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={!isCustomer && !form.customer_id} data-testid="save-sr-btn">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <Card className="p-4 sm:p-5">
        {rows.length === 0 ? <EmptyState title="No service requests" /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Ref</TableHead><TableHead>Category</TableHead><TableHead>Customer</TableHead><TableHead>Priority</TableHead>
                <TableHead>Target</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id} className="cursor-pointer" data-testid={`sr-row-${s.ref}`}>
                    <TableCell className="font-num text-xs" onClick={() => setActive(s)}>{s.ref} {s.escalated && <AlertTriangle className="inline h-3 w-3 text-red-500" />}</TableCell>
                    <TableCell onClick={() => setActive(s)}>{s.category}</TableCell>
                    <TableCell onClick={() => setActive(s)}>{s.customer_name}</TableCell>
                    <TableCell className="capitalize" onClick={() => setActive(s)}>{s.priority}</TableCell>
                    <TableCell className="font-num text-xs" onClick={() => setActive(s)}>{s.target_date || "—"}</TableCell>
                    <TableCell onClick={() => setActive(s)}><StatusBadge status={s.status} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canEdit && (
                        <Select value={s.status} onValueChange={(v) => setStatus(s.id, v)}>
                          <SelectTrigger className="h-8 w-32 inline-flex" data-testid={`sr-status-${s.ref}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{st.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      {canEdit && !s.escalated && <Button size="sm" variant="ghost" onClick={() => escalate(s.id)} data-testid={`escalate-${s.ref}`}>Escalate</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {active && (
            <>
              <SheetHeader><SheetTitle>{active.ref} · {active.category}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{active.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Owner</span><span>{active.owner_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={active.status} /></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Priority</span><span className="capitalize">{active.priority}</span></div>
                <div className="pt-3 border-t border-border">
                  <h4 className="font-semibold mb-2">Comments</h4>
                  {(active.comments || []).map((c, i) => (
                    <div key={i} className="mb-2 text-xs bg-secondary rounded-lg p-2"><div className="font-medium">{c.by}</div>{c.text}</div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add comment…" data-testid="sr-comment-input" />
                    <Button size="sm" onClick={addComment} data-testid="sr-comment-btn">Post</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
